<template>
  <button
    type="button"
    :class="[
      'rounded-lg border-2 border-solid p-4 flex flex-col gap-3 flex-1 min-w-[180px] text-left cursor-pointer transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
      active ? [config.border, config.activeBackground] : 'border-gray-200 bg-white'
    ]"
    :aria-pressed="active ? 'true' : 'false'"
    :data-testid="`summary-card-${config.key}`"
    @click="emit('toggle')"
  >
    <span class="flex w-full items-center gap-1 text-sm font-medium" :class="config.textColor">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-4 h-4"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path :d="config.icon" fill="currentColor" />
      </svg>
      <span class="text-lg text-textPrimary font-bold">{{ config.label }}</span>
    </span>
    <span class="text-left text-3xl font-bold text-textPrimary" data-testid="summary-card-count">
      {{ count }}
    </span>
  </button>
</template>

<!--
  `border-solid` é obrigatório: o reset do Vuetify (`button { border-style: none }`)
  vence o `* { border-style: solid }` do preflight do Tailwind, e sem ele a borda
  do <button> não aparece (no antigo <div> ela vinha do preflight).
-->
<script setup lang="ts">
import { computed } from 'vue'
import { mdiLock, mdiLockOpenVariant } from '@mdi/js'
import { ExceptionStatusEnum } from '@/Network/Types/Requests/DoctorStationExceptionRequests'

// Inativo: borda cinza. Ativo (status na seleção do filtro): borda + fundo claro na cor do status.
interface StatusConfig {
  border: string
  activeBackground: string
  textColor: string
  icon: string
  label: string
  key: string
}

const props = defineProps<{
  status: ExceptionStatusEnum
  count: number
  /** O status do bloco está na seleção do filtro de status da lista. */
  active: boolean
}>()

const emit = defineEmits<{
  toggle: []
}>()

// Classes completas (sem interpolar) para o JIT do Tailwind enxergá-las.
const STATUS_MAP: Record<string, StatusConfig> = {
  [ExceptionStatusEnum.AWAITING_APPROVAL]: {
    border: 'border-alert',
    activeBackground: 'bg-alert/10',
    textColor: 'text-alert',
    icon: mdiLock,
    label: 'Em andamento',
    key: 'in-progress'
  },
  [ExceptionStatusEnum.APPROVED]: {
    border: 'border-sucess',
    activeBackground: 'bg-sucess/10',
    textColor: 'text-sucess',
    icon: mdiLockOpenVariant,
    label: 'Aprovado',
    key: 'approved'
  },
  [ExceptionStatusEnum.REJECTED]: {
    border: 'border-negative',
    activeBackground: 'bg-negative/10',
    textColor: 'text-negative',
    icon: mdiLock,
    label: 'Recusado',
    key: 'rejected'
  }
}

const DEFAULT_CONFIG: StatusConfig = {
  border: 'border-gray-300',
  activeBackground: 'bg-gray-100',
  textColor: 'text-gray-500',
  icon: mdiLock,
  label: 'Desconhecido',
  key: 'unknown'
}

const config = computed(() => STATUS_MAP[props.status] ?? DEFAULT_CONFIG)
</script>
